<template>
  <v-dialog
    persistent
    scrollable
    :value="value"
    max-width="640"
    @input="$emit('input', $event)"
  >
    <v-card>
      <v-card-title class="success white--text py-2 px-4">
        <span class="title">{{ title }}</span>
        <v-spacer></v-spacer>
        <v-btn icon flat small dark @click="$emit('input', false)">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-card-text class="pa-4">
        <div class="mb-3">{{ intro }}</div>

        <div v-if="loading" class="grey--text">
          <v-progress-linear indeterminate color="success" class="mb-2"></v-progress-linear>
          Checking how pay will change…
        </div>

        <div v-else-if="!days.length" class="grey--text">
          Pay will not change.
        </div>

        <div
          v-for="day in days"
          :key="dayKey(day)"
          class="mb-3 pa-3 impact-day"
        >
          <div class="font-weight-medium">
            {{ day.driver || "Driver" }}
            <span v-if="day.busNumber" class="grey--text">
              · Bus {{ day.busNumber }}
            </span>
          </div>
          <div class="mt-1">{{ day.summary }}</div>

          <v-radio-group
            v-if="day.requiresDecision && day.situation === 'orphanDesk'"
            v-model="choices[dayKey(day)].action"
            hide-details
            class="mt-2"
          >
            <v-radio
              color="success"
              value="keep"
              :label="'Keep the ' + money(day.currentAmount) + ' on the books'"
            ></v-radio>
            <v-radio
              color="success"
              value="clear"
              label="Remove the deduction"
            ></v-radio>
          </v-radio-group>

          <v-radio-group
            v-else-if="day.requiresDecision"
            v-model="choices[dayKey(day)].preset"
            hide-details
            class="mt-2"
            @change="onPresetPicked(day)"
          >
            <v-radio
              v-if="day.allowKeep"
              color="success"
              value="__keep__"
              :label="
                'Keep the current ' + money(day.currentAmount) + ' desk amount'
              "
            ></v-radio>
            <v-radio
              v-for="suggestion in (day.suggestions || []).filter((s) => s.preset !== 'NO_DEDUCT')"
              :key="dayKey(day) + suggestion.preset"
              color="success"
              :value="suggestion.preset"
              :label="suggestion.label + ' — ' + money(suggestion.amount)"
            ></v-radio>
            <v-radio
              color="success"
              value="MANUAL"
              label="Custom amount"
            ></v-radio>
            <v-radio
              v-for="suggestion in (day.suggestions || []).filter((s) => s.preset === 'NO_DEDUCT')"
              :key="dayKey(day) + suggestion.preset"
              color="success"
              :value="suggestion.preset"
              :label="suggestion.label + ' — ' + money(suggestion.amount)"
            ></v-radio>
          </v-radio-group>

          <v-text-field
            v-if="
              day.requiresDecision &&
                choices[dayKey(day)] &&
                choices[dayKey(day)].preset === 'MANUAL'
            "
            v-model="choices[dayKey(day)].customAmount"
            label="Amount"
            type="number"
            prefix="$"
            class="mt-2"
          ></v-text-field>
          <v-text-field
            v-if="
              day.requiresDecision &&
                choices[dayKey(day)] &&
                choices[dayKey(day)].preset === 'MANUAL'
            "
            v-model="choices[dayKey(day)].customReason"
            label="Reason (optional)"
            class="mt-2"
          ></v-text-field>

          <v-text-field
            v-if="
              day.requiresDecision &&
                choices[dayKey(day)] &&
                choices[dayKey(day)].preset === 'NO_DEDUCT'
            "
            v-model="choices[dayKey(day)].noDeductReason"
            label="Reason for no deduct"
            class="mt-2"
          ></v-text-field>
        </div>
      </v-card-text>

      <v-divider></v-divider>
      <v-card-actions class="pa-3">
        <v-spacer></v-spacer>
        <v-btn color="default" dark @click="$emit('input', false)">
          Cancel
        </v-btn>
        <v-btn
          color="success"
          :disabled="loading || !ready"
          :loading="confirmLoading"
          @click="confirm"
        >
          {{ confirmText }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
export default {
  props: {
    value: Boolean,
    title: {
      type: String,
      default: "Pay will change",
    },
    intro: {
      type: String,
      default: "",
    },
    confirmText: {
      type: String,
      default: "Continue",
    },
    loading: Boolean,
    confirmLoading: Boolean,
    days: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      choices: {},
    };
  },
  computed: {
    ready() {
      return this.days.every((day) => {
        if (!day.requiresDecision) return true;
        const choice = this.choices[this.dayKey(day)];
        if (!choice) return false;
        if (day.situation === "orphanDesk") {
          return choice.action === "keep" || choice.action === "clear";
        }
        if (!choice.preset) return false;
        if (choice.preset === "NO_DEDUCT" && !choice.noDeductReason) {
          return false;
        }
        if (
          choice.preset === "MANUAL" &&
          (choice.customAmount === "" ||
            choice.customAmount == null ||
            isNaN(Number(choice.customAmount)))
        ) {
          return false;
        }
        return true;
      });
    },
  },
  watch: {
    days: {
      immediate: true,
      handler() {
        const next = {};
        (this.days || []).forEach((day) => {
          next[this.dayKey(day)] = {
            action: "",
            preset: "",
            noDeductReason: "",
            customAmount: "",
            customReason: "",
          };
        });
        this.choices = next;
      },
    },
  },
  methods: {
    dayKey(day) {
      const date = day.date ? String(day.date).substr(0, 10) : "";
      return day.driverBusID + "|" + date + "|" + (day.kind || "");
    },
    money(amount) {
      return "$" + Number(amount || 0).toFixed(2);
    },
    onPresetPicked(day) {
      const choice = this.choices[this.dayKey(day)];
      if (!choice) return;
      choice.action = choice.preset === "__keep__" ? "keep" : "set";
    },
    confirm() {
      const decisions = this.days
        .filter((day) => day.requiresDecision)
        .map((day) => {
          const choice = this.choices[this.dayKey(day)];
          if (day.situation === "orphanDesk") {
            return {
              driverBusID: day.driverBusID,
              date: String(day.date).substr(0, 10),
              kind: day.kind || "",
              action: choice.action,
            };
          }

          if (choice.preset === "__keep__") {
            return {
              driverBusID: day.driverBusID,
              date: String(day.date).substr(0, 10),
              kind: day.kind || "",
              action: "keep",
            };
          }

          const suggestion = (day.suggestions || []).find(
            (item) => item.preset === choice.preset
          );
          return {
            driverBusID: day.driverBusID,
            date: String(day.date).substr(0, 10),
            kind: day.kind || "",
            action: "set",
            preset: choice.preset,
            amount:
              choice.preset === "MANUAL"
                ? Number(choice.customAmount)
                : suggestion
                ? suggestion.amount
                : day.recommendedAmount,
            category: day.resultCategory || day.currentCategory || "",
            noDeductReason:
              choice.preset === "MANUAL"
                ? choice.customReason
                : choice.noDeductReason,
          };
        });

      this.$emit("confirm", decisions);
    },
  },
};
</script>

<style scoped>
.impact-day {
  background-color: #fff8f0;
  border: 1px solid #ffe0b2;
  border-left: 4px solid #fb8c00;
  border-radius: 6px;
}
</style>
